/**
 * Custom hook for managing store integration mutations
 * Encapsulates common mutation patterns for Lightspeed integration
 */
import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { UPDATE_STORE_INTEGRATION, REQUEST_PUSH_MENU } from '../queries'
import { LightspeedMenuOrderType } from '../types'
import { PartnerName } from '../../../constants'

export interface DisconnectStoreVariables {
  storeId: string
}

export interface ConnectStoreVariables {
  storeId: string
  businessId: string
  businessName: string
  businessLocationId: string
  businessLocationName: string
  menus?: Array<{
    id: string
    name: string
    orderType?: LightspeedMenuOrderType
  }>
  orderProfileMapping?: {
    delivery: string | null
    pickup: string | null
    orderAtTable: string | null
  }
  sameDayBufferMinutes?: number | null
  preOrderBufferMinutes?: number | null
}

export const useStoreIntegration = () => {
  const [updateIntegration, { loading: isUpdating }] = useMutation(
    UPDATE_STORE_INTEGRATION,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const [requestMenuPush, { loading: isPushingMenu }] = useMutation(
    REQUEST_PUSH_MENU,
    {
      fetchPolicy: 'no-cache'
    }
  )

  /**
   * Disconnects a store from Lightspeed
   */
  const disconnectStore = useCallback(
    (variables: DisconnectStoreVariables) => {
      return updateIntegration({
        variables: {
          partnerName: PartnerName.LIGHTSPEED,
          storeId: variables.storeId,
          lightspeedBusinessId: null,
          lightspeedBusinessName: null,
          lightspeedBusinessLocationId: null,
          lightspeedBusinessLocationName: null,
          lightspeedMenus: [],
          lightspeedOrderProfileMapping: {
            delivery: null,
            pickup: null,
            orderAtTable: null
          }
        }
      })
    },
    [updateIntegration]
  )

  /**
   * Connects a store to Lightspeed
   */
  const connectStore = useCallback(
    (variables: ConnectStoreVariables) => {
      return updateIntegration({
        variables: {
          partnerName: PartnerName.LIGHTSPEED,
          storeId: variables.storeId,
          lightspeedBusinessId: variables.businessId,
          lightspeedBusinessName: variables.businessName,
          lightspeedBusinessLocationId: variables.businessLocationId,
          lightspeedBusinessLocationName: variables.businessLocationName,
          lightspeedMenus: variables.menus || [],
          lightspeedOrderProfileMapping: variables.orderProfileMapping || {
            delivery: null,
            pickup: null,
            orderAtTable: null
          },
          lightspeedScheduledOrderBufferSamedayMinutes:
            variables.sameDayBufferMinutes ?? null,
          lightspeedScheduledOrderBufferPreorderMinutes:
            variables.preOrderBufferMinutes ?? null
        }
      })
    },
    [updateIntegration]
  )

  /**
   * Syncs menu for a store
   */
  const syncMenu = useCallback(
    (storeId: string) => {
      return requestMenuPush({
        variables: {
          partnerName: PartnerName.LIGHTSPEED,
          storeId
        }
      })
    },
    [requestMenuPush]
  )

  return {
    disconnectStore,
    connectStore,
    syncMenu,
    isUpdating,
    isPushingMenu
  }
}
